<script lang="ts">
	import LandingPageSectionTitles from '@app/components/cms/landing-pages/LandingPageSectionTitles.svelte';
	import { onMount } from 'svelte';
	import type { CTAProps } from '@app/components/cms/types';
	import CMSCallToAction from '@app/components/cms/CMSCallToAction.svelte';
	import type { ISbStoryData } from '@storyblok/js/dist/types/types';
	import ProgressIndicator from '$lib/elements/ProgressIndicator.svelte';
	import AdaptiveImage from '$lib/components/AdaptiveImage.svelte';
	import { getOptimizedCmsImageUrl } from '@app/utils/cms';
	import ButtonText from '$lib/elements/buttons/variants/ButtonText.svelte';
	import { t } from 'svelte-i18n';
	import TabletSwipeableWrapper from '@app/components/layouts/TabletSwipeableWrapper.svelte';
	import { goto } from '$app/navigation';

	export let blok: {
		title: string;
		desc: string;
		articles: string[];
		cta: CTAProps[];
	};

	let articles: ISbStoryData[] = [];

	const getAdaptiveImagesData = (filename: string, alt: string) => {
		return {
			sources: [
				{
					media: '(max-width: 767px)',
					srcset: `${getOptimizedCmsImageUrl(filename, '280x250')} 280w, ${getOptimizedCmsImageUrl(
						filename,
						'560x500'
					)} 2x`
				},
				{
					media: '(min-width: 768px)',
					srcset: `${getOptimizedCmsImageUrl(filename, '400x250')} 400w, ${getOptimizedCmsImageUrl(
						filename,
						'800x500'
					)} 2x`
				}
			],
			img: getOptimizedCmsImageUrl(filename, '400x250'),
			alt: alt ? alt : 'wiseed'
		};
	};

	onMount(async () => {
		articles = await Promise.all(
			blok.articles.map(async (uuid: string): Promise<ISbStoryData> => {
				const res = await fetch(`/cms/${uuid}?find_by=uuid`);
				return await res.json();
			})
		);
	});
</script>

<div class="py-[64px] lg:py-[80px] bg-grey-lighter">
	<div class="container">
		<LandingPageSectionTitles {blok} />
		{#if articles.length}
			<TabletSwipeableWrapper classNames="flex lg:grid lg:grid-cols-3 gap-3 md:gap-4 py-6">
				{#each articles as a}
					<div
						class="bg-white flex flex-col gap-3 md:gap-4 p-3 md:p-4 rounded-lg w-10/12 md:w-2/5 lg:w-auto flex-shrink-0 hover:cursor-pointer"
						on:click={() => {
							goto(a.full_slug);
						}}
						on:keydown
					>
						<AdaptiveImage
							src={getAdaptiveImagesData(a.content.imageHeader.filename, a.content.imageHeader.alt)}
							classNames="rounded-lg w-full"
						/>
						<div class="font-semibold text-xl flex-1">{a.name}</div>
						<div class="line-clamp-4">{a.content.introduction}</div>
						<ButtonText
							props={{
								label: $t('blog.read'),
								iconRight: 'arrow_right',
								iconSize: 'small',
								classNames: 'border-b-2 pb-1 self-start'
							}}
						/>
					</div>
				{/each}
			</TabletSwipeableWrapper>
		{:else}
			<div class="flex items-center justify-center">
				<ProgressIndicator appearance="circular" size="huge" classNames="min-h-[200px]" />
			</div>
		{/if}
		{#if blok.cta.length}
			<div class="flex justify-center">
				<CMSCallToAction props={blok.cta[0]} />
			</div>
		{/if}
	</div>
</div>
